<template>
  <div>
    <Dialog :title="title" :value="value" @input="handleClose" custom-max-width-class="max-w-[520px]">
            <div class="px-4 pt-4 pb-4 sm:px-6">
              <form
                enctype="multipart/form-data"
                @submit.prevent="createNewPost()"
                accept-charset="UTF-8"
                method="post"
                id="new-post-form"
              >
                <input name="utf8" type="hidden" value="✓" />

                <input
                  type="hidden"
                  name="authenticity_token"
                  :value="this.form_token"
                />

                <div v-if="isAdmin">
                  <AuthorSelect
                    :label="$t('Author Name')"
                    :options="authors"
                    :current-user="currentUser"
                    v-model="postAuthor"
                    :show-options="showOptions"
                    @showAuthorEmailField="showAuthorEmailField"
                    @hideAuthorEmailField="hideAuthorEmailField"
                    @addSelectedAuthor="addSelectedAuthor"
                    @removeSelectedAuthor="removeSelectedAuthor"
                    @showDropdown="showDropdown"
                  />
                </div>

                <section v-if="authorEmailField" class="new-author-section">
                  <div class="new-author-fields">
                    <div class="new-author-field">
                      <label class="new-author-label">{{ $t("Author name") }}</label>
                      <input
                        id="author_name_display"
                        :value="postAuthor"
                        type="text"
                        class="new-author-input"
                        disabled
                      />
                    </div>
                    <div class="new-author-field">
                      <label class="new-author-label">{{ $t("Author email") }}</label>
                      <input
                        id="author_email"
                        :placeholder="$t('email@example.com')"
                        v-model="authorEmail"
                        type="email"
                        name="post[email]"
                        class="new-author-input"
                        autocomplete="off"
                        required
                      />
                    </div>
                  </div>

                  <label
                    class="new-author-checkbox"
                    v-if="!projectPrivate"
                  >
                    <input
                      type="checkbox"
                      value="1"
                      name="post[notify_user]"
                      v-model="agreeCheckbox"
                    />
                    <span>{{ $t("Notify the user about updates and comments to this idea") }}</span>
                  </label>
                </section>

                <div class="mt-3">
                  <label class="font-semibold text-sm leading-3 text-neutral-800"
                    >{{ $t("Title") }}
                  </label>
                  <input
                    id="post_title"
                    :placeholder="$t('Something short')"
                    v-model="newPostTitle"
                    autocomplete="off"
                    required="required"
                    type="text"
                    name="post[title]"
                    class="input rounded mt-1"
                  />
                </div>

                <div class="mt-3">
                  <label class="font-semibold text-sm leading-3 text-neutral-800"
                    >{{ $t("Description") }}
                  </label>
                  <RichTextEditor
                    v-model="newPostContent"
                    input-id="post_content_modal"
                    input-name="post[content]"
                    variant="minimal"
                    :placeholder="$t('Write about your idea in more detail here')"
                    :enable-image-upload="true"
                    :enable-embeds="false"
                    :enable-drafts="false"
                    upload-url="/posts/images"
                  />
                </div>

                <div v-if="error" class="text-red-700 pt-4">
                  <p>{{ error }}</p>
                </div>

                <FileUpload
                  name="post[attachments][]"
                  class="mt-3 mb-4"
                  :noun="$t('images')"
                  upy-id="post-image"
                >
                </FileUpload>
                <div v-if="isAdmin">
                  <label
                    for="board"
                    class="font-semibold text-sm leading-3 text-neutral-800"
                    >{{ $t("Board") }}
                  </label>
                  <div class="relative flex-1">
                    <select
                      id="board"
                      name="post[board]"
                      v-model="selectedBoard"
                      class="select capitalize mt-1"
                    >
                      <option
                        v-for="option in allBoards"
                        :key="option.board_id"
                        :value="option.board_id"
                        class="capitalize"
                      >
                        {{ option.board_name }}
                      </option>
                    </select>
                  </div>
                </div>

                <div class="mt-3" v-if="isAdmin">
                  <label
                    for="status"
                    class="font-semibold text-sm leading-3 text-neutral-800"
                    >{{ $t("Status") }}
                  </label>
                  <div class="relative flex-1">
                    <select
                      id="status"
                      name="post[status_id]"
                      v-model="selectedStatus"
                      class="select capitalize mt-1"
                    >
                      <option
                        v-for="status in allStatuses"
                        :key="status.id"
                        :value="status.id"
                        class="capitalize"
                      >
                        {{ status.name }}
                      </option>
                    </select>
                  </div>
                </div>

                <input
                  v-model="user_ip_address"
                  type="hidden"
                  name="post[user_ip_address]"
                />

                <div v-if="!userSignedIn">
                  <div class="mt-3" id="post-name">
                    <label class="label">{{ $t("Name") }}</label>
                    <input
                      id="name"
                      :placeholder="$t('Your name')"
                      v-model="name"
                      autocomplete="off"
                      required="required"
                      type="text"
                      name="post[name]"
                      class="input"
                    />
                  </div>
                  <div class="mt-3" id="post-email">
                    <label class="label">{{ $t("Email") }}</label>
                    <input
                      id="email"
                      :placeholder="$t('Your email address')"
                      v-model="email"
                      autocomplete="off"
                      required="required"
                      type="email"
                      name="post[email]"
                      class="input"
                    />
                  </div>

                  <div
                    class="mt-3 text-neutral-700 text-xs text-width flex items-start gap-2"
                    id="post-agree"
                  >
                    <input name="user[agree_checkbox]" type="hidden" value="0" />
                    <input
                      required="required"
                      type="checkbox"
                      value="1"
                      name="user[agree_checkbox]"
                      id="user_agree_checkbox"
                      v-model="agree_checkbox"
                      class="mt-0.5 h-4 w-4 shrink-0"
                    />
                    <div>
                      <span>{{ $t("I agree with FeedBear's") }}</span>
                      <a
                        href="https://www.feedbear.com/privacy-policy"
                        :title="$t('Privacy Policy')"
                        target="_blank"
                        class="text-neutral-600 underline hover:no-underline hover:text-neutral-600"
                        >{{ $t("Privacy Policy") }}</a
                      >
                      {{ $t("and") }}
                      <a
                        href="https://www.feedbear.com/terms"
                        :title="$t('Terms of Service')"
                        target="_blank"
                        class="text-neutral-600 underline hover:no-underline hover:text-neutral-600"
                        >{{ $t("Terms and Conditions") }}</a
                      >
                    </div>
                  </div>
                </div>
                <input
                  :value="new Date()"
                  type="hidden"
                  name="post[agreed_with_privacy_policy]"
                  id="user_agreed_with_privacy_policy"
                />

                <div class="mt-3 flex justify-end">
                  <button
                    id="roadmap-idea-submit"
                    name="button"
                    type="submit"
                    class="inline-flex items-center justify-center text-white rounded-lg shadow-sm px-4 py-2 text-sm font-medium"
                    style="background-color: var(--accent-color)"
                  >
                    <span>{{ $t("Add idea") }}</span>
                    <div v-if="loading" class="flex items-center h-full">
                      <loader :border-color="loaderColor" />
                    </div>
                  </button>
                </div>
              </form>
            </div>
    </Dialog>
  </div>
</template>

<script>
import Dialog from "../components/dialog";
import FileUpload from "../components/file_upload";
import { RichTextEditor } from "../components/rich_text_editor";
import AuthorSelect from "./custom_select";
import loader from "../projects/spinner";
import { mapGetters, mapActions } from "vuex";

export default {
  props: [
    "title",
    "value",
    "form_action",
    "form_token",
    "allStatuses",
    "allBoards",
    "current-user",
    "idea-creation-url",
    "project-color",
    "project-private",
    "isAdmin",
    "user-signed-in",
    "currentProjectUsersUrl",
  ],
  data() {
    return {
      postAuthor: "",
      authorEmail: "",
      newPostTitle: "",
      newPostContent: "",
      selectedAuthor: this.currentUser.id,
      selectedStatus: this.allStatuses[0].id,
      selectedBoard: this.allBoards[0].board_id,
      agreeCheckbox: false,
      authorEmailField: false,
      user_ip_address: "",
      loading: false,
      loaderColor: "#fff",
      showOptions: true,
      name: "",
      email: "",
      agree_checkbox: false,
      error: "",
    };
  },
  components: {
    Dialog,
    FileUpload,
    RichTextEditor,
    AuthorSelect,
    loader,
  },
  created() {
  },
  async mounted() {
    if (this.userSignedIn && this.isAdmin) {
      await this.$store.dispatch(
        "getcurrentProjectUsers",
        this.currentProjectUsersUrl
      );
    }
    // Loader stays white since button is always accent-colored
    this.loaderColor = "#fff";

    document.body.classList.add("overflow-hidden");
  },
  computed: {
    ...mapGetters(["allUsers"]),
    authors() {
      return this.allUsers;
    },
  },
  methods: {
    ...mapActions(["getcurrentProjectUsers"]),
    handleClose() {
      document.body.classList.remove("overflow-hidden");
      this.resetAuthorFields();
      this.$emit("input", false);
    },

    resetAuthorFields() {
      this.authorEmailField = false;
      this.postAuthor = "";
    },

    resetPostFields() {
      this.newPostTitle = "";
      this.newPostContent = "";
    },

    showAuthorEmailField(postAuthor) {
      this.postAuthor = postAuthor;
      this.authorEmailField = true;
    },

    showDropdown(value) {
      this.showOptions = value;
    },

    hideAuthorEmailField() {
      this.authorEmailField = false;
      this.postAuthor = "";
    },

    addSelectedAuthor(authorId) {
      this.selectedAuthor = authorId;
      this.postAuthor = "";
    },

    removeSelectedAuthor() {
      this.selectedAuthor = null;
    },

    createNewPost() {
      this.loading = true;

      var formData = new FormData(document.getElementById("new-post-form"));

      if (this.currentUser) {
        if (this.selectedAuthor) {
          formData.append("post[author]", this.selectedAuthor);
        } else {
          formData.append("post[name]", this.postAuthor);
          if (!this.agreeCheckbox) formData.append("dont_notify_author", true);
        }
      }

      Rails.ajax({
        url: this.ideaCreationUrl,
        type: "POST",
        data: formData,
        dataType: "json",
        beforeSend: function () {
          return true;
        },
        success: (data) => {
          this.$emit("updateRoadmap", {
            message: data.message,
            url: data.idea_url,
          });
          this.loading = false;
          this.resetPostFields();
          this.resetAuthorFields();
          this.handleClose();
        },
        error: (data, status) => {
          this.loading = false;
          this.error = data.error;
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>

.new-author-section {
  margin-top: 12px;
  padding: 14px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
}

.new-author-fields {
  display: flex;
  gap: 12px;
}

.new-author-field {
  flex: 1;
}

.new-author-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #404040;
  margin-bottom: 6px;
}

.new-author-input {
  width: 100%;
  padding: 10px 12px;
  font-size: 13px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  outline: none;
  transition: all 0.15s ease;

  &::placeholder {
    color: #a3a3a3;
  }

  &:focus {
    border-color: var(--accent-color, #009be5);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent-color, #009be5) 15%, transparent);
  }

  &:disabled {
    background: #f3f4f6;
    color: #737373;
    cursor: not-allowed;
  }
}

.new-author-checkbox {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 12px;
  font-size: 12px;
  color: #737373;
  cursor: pointer;

  input[type="checkbox"] {
    width: 16px;
    height: 16px;
    margin-top: 1px;
    border-radius: 4px;
    cursor: pointer;
    accent-color: var(--accent-color, #009be5);
  }

  span {
    line-height: 1.4;
  }
}

@media screen and (max-width: 480px) {
  .new-author-fields {
    flex-direction: column;
  }
}

</style>
